import React, { useMemo } from "react";
import { Card, Popover, Skeleton, Typography } from "antd";
import {
  CaretDownOutlined,
  CaretUpOutlined,
  InfoCircleOutlined,
  MinusOutlined,
} from "@ant-design/icons";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
} from "chart.js";
import { Line } from "react-chartjs-2";
import { themeColors, themeLayout, themeText } from "theme";
import { getSparklineTrend } from "views/breakage/breakage-dashboard-aggregates";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip
);

const { Text, Title } = Typography;

const trendStyle = {
  up: { color: themeColors.successColor, icon: <CaretUpOutlined /> },
  down: { color: themeColors.errorColor, icon: <CaretDownOutlined /> },
  flat: { color: themeColors.grayColor, icon: <MinusOutlined /> },
};

const BreakageKpiCard = ({
  title,
  value,
  meta,
  count,
  accent = themeColors.lightColor,
  icon,
  sparkline = [],
  trend,
  loading = false,
  countUnit = "saldo",
  footer = null,
  showSparkline = true,
  help,
}) => {
  const useSparkline = showSparkline && !footer;
  const resolvedTrend = useMemo(() => {
    if (trend?.direction) {
      return trend;
    }
    if (!useSparkline) {
      return { direction: "flat" };
    }
    return getSparklineTrend(sparkline);
  }, [trend, sparkline, useSparkline]);

  const chart = useMemo(() => {
    if (!useSparkline) {
      return null;
    }
    const points = sparkline.length > 0 ? sparkline : [0, 0];
    return {
      data: {
        labels: points.map((_, i) => String(i + 1)),
        datasets: [
          {
            data: points,
            borderColor: accent,
            backgroundColor: `${accent}22`,
            borderWidth: 2,
            fill: true,
            tension: 0,
            pointRadius: 1,
            pointHoverRadius: 3,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          datalabels: { display: false },
          tooltip: { enabled: false },
        },
        scales: {
          x: { display: false },
          y: { display: false },
        },
        elements: {
          line: { borderJoinStyle: "round" },
        },
      },
    };
  }, [sparkline, accent, useSparkline]);

  const t = trendStyle[resolvedTrend.direction] || trendStyle.flat;
  const countLabel =
    count !== undefined && count !== null
      ? `${count} ${countUnit}${Number(count) === 1 ? "" : "s"}`
      : null;
  const secondaryLabel = countLabel || meta || null;

  return (
    <Card
      bordered={false}
      style={{
        height: "100%",
        borderRadius: themeLayout.borderRadius,
        boxShadow: themeLayout.boxShadow,
        background: "#fff",
      }}
      bodyStyle={{
        padding: 16,
        display: "flex",
        flexDirection: "column",
        minHeight: 150,
      }}
    >
      {loading ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 10, flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <Skeleton.Avatar active size={36} shape="square" />
            <Skeleton.Input active size="small" style={{ width: 96, minWidth: 96 }} />
          </div>
          <Skeleton.Input active size="large" style={{ width: "70%", minWidth: 120 }} />
          <Skeleton.Input active size="small" style={{ width: 80, minWidth: 80 }} />
          <Skeleton.Node active style={{ width: "100%", height: 44, marginTop: "auto" }}>
            <span />
          </Skeleton.Node>
        </div>
      ) : (
        <>
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 8,
              marginBottom: 8,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                minWidth: 0,
                flex: 1,
              }}
            >
              {icon && (
                <div
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 8,
                    background: `${accent}1A`,
                    color: accent,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 16,
                    flexShrink: 0,
                  }}
                >
                  {icon}
                </div>
              )}
              <Text
                style={{
                  color: themeColors.grayColor,
                  fontFamily: themeText.fontFamily,
                  fontWeight: themeText.medium,
                  fontSize: 13,
                  lineHeight: 1.35,
                }}
              >
                {title}
              </Text>
            </div>
            {help && (
              <Popover
                title={title}
                content={
                  <Text
                    style={{
                      display: "block",
                      maxWidth: 280,
                      fontSize: 13,
                      lineHeight: 1.5,
                      color: themeColors.mediumColor,
                      fontFamily: themeText.fontFamily,
                    }}
                  >
                    {help}
                  </Text>
                }
                trigger={["hover", "click", "focus"]}
                mouseEnterDelay={0.25}
                placement="topRight"
              >
                <button
                  type="button"
                  aria-label={`Saiba mais sobre ${title}`}
                  style={{
                    width: 22,
                    height: 22,
                    padding: 0,
                    border: "none",
                    borderRadius: "50%",
                    background: "transparent",
                    color: themeColors.grayColor,
                    cursor: "help",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    fontSize: 14,
                    lineHeight: 1,
                    marginTop: 2,
                  }}
                >
                  <InfoCircleOutlined />
                </button>
              </Popover>
            )}
          </div>

          <Title
            level={3}
            style={{
              margin: 0,
              color: themeColors.darkColor,
              fontFamily: themeText.fontFamily,
              fontWeight: themeText.extra,
              fontSize: 22,
              lineHeight: 1.2,
            }}
          >
            {value}
          </Title>

          {secondaryLabel && (
            <Text
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                marginTop: 4,
                color: t.color,
                fontSize: 12,
                fontWeight: themeText.medium,
              }}
            >
              <span style={{ color: themeColors.mediumColor }}>
                {countLabel || meta}
                {countLabel && meta ? ` · ${meta}` : ""}
              </span>
              {useSparkline && countLabel && (
                <span
                  aria-label={
                    resolvedTrend.direction === "up"
                      ? "subindo"
                      : resolvedTrend.direction === "down"
                        ? "descendo"
                        : "estável"
                  }
                  style={{ display: "inline-flex", alignItems: "center", gap: 2 }}
                >
                  {t.icon}
                </span>
              )}
            </Text>
          )}

          {footer ? (
            <div style={{ marginTop: "auto", paddingTop: 12 }}>{footer}</div>
          ) : (
            useSparkline &&
            chart && (
              <div style={{ height: 44, marginTop: "auto", paddingTop: 10 }}>
                <Line {...chart} />
              </div>
            )
          )}
        </>
      )}
    </Card>
  );
};

export default BreakageKpiCard;
